// src/pages/User/EditOperator.tsx

import {
  AssignmentIndTwoTone,
  EditLocationTwoTone,
  FaceTwoTone,
  HttpTwoTone,
  MailTwoTone,
  MoreVertTwoTone,
  PhoneTwoTone,
  PrintTwoTone,
  SaveTwoTone,
  SmartphoneTwoTone,
  VpnKeyTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Avatar,
  Button,
  Collapse,
  Container,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { isValidEmail, isValidLength, isValidPassword } from '../../api'
import avatar from '../../assets/img/default-avatar.png'
import {
  AM,
  AMItem,
  KPage,
  KSelect,
  KTextField,
  DlgProfilePicture,
  PushDevices,
  OperatorAbos,
} from '../../components'
import {
  OperatorData,
  useOperator,
  useOperatorApi,
  useUserRoles,
} from '../../hooks'

export const EditOperator = () => {
  const { tabId } = useParams()

  const { update: updateOperator, updatePassword } = useOperatorApi()

  const userRolePool = useUserRoles()

  const [tab, setTab] = useState(Number(tabId) || 0)
  const [userId, setUserId] = useState(null)
  const [userData, setUserData] = useState<any>({})

  const [fromPassword, setFromPassword] = useState('')
  const [toPassword, setToPassword] = useState('')

  const [actionMenu, showActionMenu] = useState(null)

  const opData: OperatorData = useOperator()

  useEffect(() => {
    if (opData) {
      const {
        city,
        country,
        email,
        email_contact,
        fax,
        first_name,
        homepage,
        id,
        image_url,
        last_name,
        telephone,
        telephone_mobile,
        role_id,
        street,
        zip,
      } = opData

      let role
      for (const item of userRolePool.filter(f => Number(f.id) === role_id)) {
        const { label } = item.attributes
        role = `${label}`
      }

      setUserData({
        city,
        country,
        email,
        email_contact,
        fax,
        first_name,
        homepage,
        image_url,
        last_name,
        telephone,
        telephone_mobile,
        role,
        street,
        zip,
      })

      setUserId(id)
    }
  }, [opData, userRolePool])

  const [openProfilePicture, setOpenProfilePicture] = useState(false)

  return (
    <>
      {/* profile picture */}
      <DlgProfilePicture
        open={openProfilePicture}
        onClose={() => setOpenProfilePicture(false)}
      />
      {/* profile picture end */}

      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Benutzer Profil'}
        open={Boolean(actionMenu)}
        onClose={() => showActionMenu(null)}
        historyUrlTarget={'users'}
      >
        <AMItem
          caption={'Speichern'}
          disabled={!userData.first_name || !userData.last_name}
          icon={<SaveTwoTone />}
          onClick={() => updateOperator(userId, userData)}
        />
      </AM>
      {/* action menu end */}

      <KPage
        avatar={
          <Avatar
            onClick={() => setOpenProfilePicture(true)}
            src={userData.image_url ? String(userData.image_url) : avatar}
          />
        }
        action={
          <IconButton onClick={event => showActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        title={`${userData.first_name} ${userData.last_name}`}
      >
        <Tabs
          centered
          indicatorColor={'primary'}
          onChange={(event, newTab) => setTab(newTab)}
          sx={{ my: 5 }}
          textColor={'primary'}
          value={tab}
        >
          <Tab label={'Stammdaten'} />
          <Tab label={'Passwort'} />
          <Tab label={'Push Geräte'} />
          <Tab label={'Berichte Abos'} />
        </Tabs>

        {/* Stammdaten */}
        <Collapse in={tab === 0}>
          <form
            name={'form'}
            onSubmit={event => {
              event.preventDefault()
              event.stopPropagation()
            }}
          >
            {/* Vorname */}
            <KTextField
              error={!isValidLength(userData.first_name, 2)}
              icon={<FaceTwoTone />}
              label={`Vorname`}
              onChange={event => {
                const first_name = event.target.value
                setUserData(prev => ({ ...prev, first_name }))
              }}
              required
              type={'text'}
              value={userData.first_name || ''}
            />

            {/* Name */}
            <KTextField
              error={!isValidLength(userData.last_name, 2)}
              icon={<FaceTwoTone />}
              label={`Name`}
              onChange={event => {
                const last_name = event.target.value
                setUserData(prev => ({ ...prev, last_name }))
              }}
              required
              type={'text'}
              value={userData.last_name || ''}
            />

            {/* Rolle */}
            <KTextField
              disabled
              icon={<AssignmentIndTwoTone />}
              label={`Rolle`}
              type={'text'}
              value={userData.role || ''}
            />

            {/* KarmaID */}
            <KTextField
              disabled
              error={!isValidEmail(userData.email)}
              helperText={
                'bei Registrierung verwendete E-Mail (nicht änderbar)'
              }
              icon={<MailTwoTone />}
              label={`KarmaID`}
              required
              value={userData.email || ''}
            />

            {/* Kontakt E-Mail */}
            <KTextField
              error={
                Boolean(userData.email_contact) &&
                !isValidEmail(userData.email_contact)
              }
              icon={<MailTwoTone />}
              label={`Kontakt E-Mail`}
              onChange={event => {
                const email_contact = event.target.value
                setUserData(prev => ({ ...prev, email_contact }))
              }}
              type={'email'}
              value={userData.email_contact || ''}
            />

            {/* Telefon Büro */}
            <KTextField
              icon={<PhoneTwoTone />}
              label={`Telefon Büro`}
              onChange={event => {
                const telephone = event.target.value
                setUserData(prev => ({ ...prev, telephone }))
              }}
              type={'tel'}
              value={userData.telephone || ''}
            />

            {/* Telefon Mobil */}
            <KTextField
              icon={<SmartphoneTwoTone />}
              label={`Telefon Mobil`}
              onChange={event => {
                const telephone_mobile = event.target.value
                setUserData(prev => ({ ...prev, telephone_mobile }))
              }}
              type={'tel'}
              value={userData.telephone_mobile || ''}
            />

            {/* Fax */}
            <KTextField
              icon={<PrintTwoTone />}
              label={`Fax`}
              onChange={event => {
                const fax = event.target.value
                setUserData(prev => ({ ...prev, fax }))
              }}
              type={'tel'}
              value={userData.fax || ''}
            />

            {/* Homepage */}
            <KTextField
              icon={<HttpTwoTone />}
              label={`Homepage`}
              onChange={event => {
                const homepage = event.target.value
                setUserData(prev => ({ ...prev, homepage }))
              }}
              type={'url'}
              value={userData.homepage || ''}
            />

            {/* Anschrift */}
            <KTextField
              icon={<EditLocationTwoTone />}
              label={`Anschrift`}
              onChange={event => {
                const street = event.target.value
                setUserData(prev => ({ ...prev, street }))
              }}
              type={'text'}
              value={userData.street || ''}
            />

            {/* PLZ */}
            <KTextField
              icon={<EditLocationTwoTone />}
              label={`PLZ`}
              onChange={event => {
                const zip = event.target.value
                setUserData(prev => ({ ...prev, zip }))
              }}
              type={'number'}
              value={userData.zip || ''}
            />

            {/* Ort */}
            <KTextField
              icon={<EditLocationTwoTone />}
              label={`Ort`}
              onChange={event => {
                const city = event.target.value
                setUserData(prev => ({ ...prev, city }))
              }}
              type={'text'}
              value={userData.city || ''}
            />

            {/* Land */}
            <KSelect
              icon={<EditLocationTwoTone />}
              label={'Land'}
              onChange={event => {
                const country = event.target.value
                setUserData(prev => ({ ...prev, country }))
              }}
              type={'text'}
              value={userData.country || ''}
            >
              <MenuItem value={'DEU'}>Deutschland</MenuItem>
              <MenuItem value={'AUS'}>Österreich</MenuItem>
              <MenuItem value={'CHE'}>Schweiz</MenuItem>
            </KSelect>
          </form>
        </Collapse>

        {/* Passwort */}
        <Collapse in={tab === 1}>
          <form
            name={'pw_form'}
            onSubmit={event => {
              event.preventDefault()

              if (
                isValidPassword(fromPassword) &&
                isValidPassword(toPassword)
              ) {
                updatePassword(fromPassword, toPassword)

                // reset form
                setFromPassword('')
                setToPassword('')
              }
            }}
          >
            {/* leave the following input field for accessibility; */}
            {/* https://goo.gl/9p2vKq */}
            <input
              autoComplete={'username'}
              defaultValue={userData.email || ''}
              hidden
              type={'text'}
            />

            {/* aktuelles Password */}
            <KTextField
              autoComplete={'current-password'}
              error={!isValidPassword(fromPassword) && fromPassword !== ''}
              icon={<VpnKeyTwoTone />}
              label={`Altes Passwort`}
              onChange={event => setFromPassword(event.target.value)}
              type={'password'}
              value={fromPassword}
            />

            {/* neues Password */}
            <KTextField
              autoComplete={'new-password'}
              error={!isValidPassword(toPassword) && toPassword !== ''}
              icon={<VpnKeyTwoTone />}
              label={`Neues Passwort`}
              onChange={event => setToPassword(event.target.value)}
              type={'password'}
              value={toPassword}
            />

            <Container sx={{ mt: 2, mb: 5, textAlign: 'right' }}>
              <Button
                disabled={
                  !isValidPassword(fromPassword) || !isValidPassword(toPassword)
                }
                type={'submit'}
              >
                Aktualisieren
              </Button>
            </Container>

            <Alert severity={'info'}>
              <AlertTitle>Komplexitätsanforderung für Passworte</AlertTitle>
              <Typography>
                &bull; Passworte müssen aus mindestens acht Zeichen bestehen
                <br />
                &bull; Passworte müssen mindestens eine Ziffer enthalten
                <br />
                &bull; Passworte müssen mindestens einen Groß- und einen
                Kleinbuchstaben enthalten
              </Typography>
            </Alert>
          </form>
        </Collapse>

        {/* Push Geräte */}
        <Collapse in={tab === 2}>
          <PushDevices />
        </Collapse>

        {/* Berichte Abos */}
        <Collapse in={tab === 3}>
          <Alert severity={'info'}>
            <AlertTitle>Übersicht Berichte Abonnements</AlertTitle>
            <Typography>
              Abonnements werden am 1. eines jeden Monats automatisiert für den
              Vormonat erstellt und per Mail verschickt. Die Zustellung erfolgt
              gegen 08:00 Uhr.
            </Typography>

            <Typography>
              Gebäudesensoren können für die Berichterstellung beliebig an- oder
              abgewählt werden.
            </Typography>
          </Alert>

          <OperatorAbos />
        </Collapse>
      </KPage>
    </>
  )
}
